import { Col, Form, Row } from "react-bootstrap";
import AudienceCard from "./AudienceCard";
import { useMemo, useState } from "react";
import CenteredModal from "components/core/basic/CenteredModal";
import Pagination from "../Pagination";
import AudienceForm from "./AudienceForm";
import { Button } from "components/core/basic/Button";
import { useAudienceTemplates } from "./useAudienceTemplates";
import { useEnabledModules, useModuleEnabled } from "redux/selectors/modules";
import checkModule from "utils/checkModule";
import { Link } from "react-router-dom";
import { HStack } from "../../../../styled-system/jsx";
import { buttonRecipe } from "components/core/basic/Button";
import { styled } from "../../../../styled-system/jsx";

const StyledLink = styled(Link, buttonRecipe);

const PAGE_SIZE = 4;

const AudiencesSection = () => {
  const [audienceSearch, setAudienceSearch] = useState("");
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [showCustomAudienceModal, setShowCustomAudienceModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const userModules = useEnabledModules();
  const hasAMCFirstPartyData = useModuleEnabled("AMC_FIRSTPARTY");

  const { templates } = useAudienceTemplates();

  const filteredAudiences = useMemo(() => {
    const filteredAudiences =
      templates?.filter((audience) => {
        if (audience.required_module) {
          return checkModule(userModules, audience.required_module);
        }
        return true;
      }) ?? [];
    if (audienceSearch) {
      return filteredAudiences.filter((audience) => {
        const { audience_template_name, audience_byline, audience_display_name, audience_description } = audience;

        return `${audience_template_name} ${audience_byline} ${audience_display_name} ${audience_description}`
          .toLowerCase()
          .includes(audienceSearch.toLowerCase());
      });
    }
    return filteredAudiences;
  }, [audienceSearch, templates, userModules]);

  const paginatedAudiences = useMemo(() => {
    return filteredAudiences?.slice((currentPage - 1) * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + PAGE_SIZE);
  }, [filteredAudiences, currentPage]);

  return (
    <Row className="pb-5">
      <Col xs={12}>
        <div className="d-flex justify-content-between">
          <h3 className="m-0 py-2 ps-5 fs-2" style={{ fontWeight: 600 }}>
            Audience Templates
          </h3>
          <CenteredModal
            show={showCustomAudienceModal}
            closeModal={() => setShowCustomAudienceModal(false)}
            title="Request a Custom Audience"
            dialogClassName="modal-60w"
            content={
              <Row className="pb-5 d-flex justify-content-center gap-3">
                <p className="text-center">
                  To have a custom audience template created, please contact your customer success representative and
                  provide a description of the template you would like to have generated.
                </p>
                <Button onPress={() => setShowCustomAudienceModal(false)}>Continue</Button>
              </Row>
            }
          />
          <HStack gap="3">
            {hasAMCFirstPartyData && <StyledLink to="/user/amc/configuration">Manage First Party Data</StyledLink>}
            <Button onPress={() => setShowCustomAudienceModal(true)}>Request a Custom Template</Button>
          </HStack>

          <CenteredModal
            show={showAudienceForm}
            closeModal={() => setShowAudienceForm(false)}
            dialogClassName="modal-60w"
            content={
              <AudienceForm
                setShowAudienceForm={setShowAudienceForm}
                defaultFormData={
                  typeof showAudienceForm === "string" ?
                    {
                      template_name: showAudienceForm,
                    }
                  : {}
                }
              />
            }
          />
        </div>
        <Row>
          <Col xs={12} md={6} xl={4} className="py-3 px-5">
            <Form.Control
              type="search"
              placeholder={"Search for an available audience..."}
              onChange={(e) => {
                setAudienceSearch(e.target.value);
              }}
              style={{ borderRadius: "5px" }}
            />
          </Col>
          {filteredAudiences?.length > PAGE_SIZE && (
            <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
              <Pagination
                data={filteredAudiences}
                pageSize={PAGE_SIZE}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Col>
          )}
        </Row>
        <Row>
          {paginatedAudiences?.map((audience, index) => {
            return <AudienceCard audience={audience} key={index} setShowAudienceForm={setShowAudienceForm} />;
          })}
        </Row>
      </Col>
    </Row>
  );
};

export default AudiencesSection;
